import { COMMAND_LINKS } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export function CommandPalette({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [q, setQ] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();

	const results = useMemo(() => {
		const needle = q.trim().toLowerCase();
		if (!needle) return COMMAND_LINKS.slice(0, 12);
		return COMMAND_LINKS.filter(
			(l) =>
				l.label.toLowerCase().includes(needle) ||
				l.to.toLowerCase().includes(needle),
		).slice(0, 12);
	}, [q]);

	useEffect(() => {
		if (open) {
			setQ("");
			requestAnimationFrame(() => inputRef.current?.focus());
		}
	}, [open]);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				onOpenChange(!open);
			}
			if (e.key === "Escape" && open) onOpenChange(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onOpenChange]);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[12vh] backdrop-blur-sm"
			role="dialog"
			aria-modal
			aria-label="Command palette"
			onClick={() => onOpenChange(false)}
		>
			<div
				className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-lift"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-center gap-2 border-b border-border/50 px-3 py-2.5">
					<Search className="size-4 text-muted-foreground" />
					<input
						ref={inputRef}
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Go to… Portfolio, Borrow, Analytics"
						className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
					/>
					<button
						type="button"
						className="rounded p-1 text-muted-foreground hover:text-foreground"
						onClick={() => onOpenChange(false)}
						aria-label="Close"
					>
						<X className="size-4" />
					</button>
				</div>
				<ul className="max-h-72 overflow-y-auto p-1.5">
					{results.length === 0 && (
						<li className="px-3 py-6 text-center text-sm text-muted-foreground">
							No matches
						</li>
					)}
					{results.map((item) => (
						<li key={item.to}>
							<button
								type="button"
								className={cn(
									"flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm hover:bg-primary/10",
								)}
								onClick={() => {
									navigate(item.to);
									onOpenChange(false);
								}}
							>
								<span className="font-semibold">{item.label}</span>
								<span className="font-mono text-[10px] text-muted-foreground">
									{item.to}
								</span>
							</button>
						</li>
					))}
				</ul>
				<p className="border-t border-border/40 px-3 py-2 font-mono text-[10px] text-muted-foreground">
					Writes still require Connect · browse is open
				</p>
			</div>
		</div>
	);
}
